import { tokenManager } from "./tokenManager";
import { clearAppRole } from "./appRole.js";

export const AUTH_SESSION_MESSAGE_KEY = "auth_session_message";

const SESSION_END_CODES = new Set([
  "AUTH_REQUIRED",
  "ACCOUNT_INACTIVE",
  "ROLE_INACTIVE",
  "SESSION_EXPIRED",
]);

export function messageForLoginFailure(code) {
  if (code === "ACCOUNT_INACTIVE") {
    return "User account is inactive. Contact administrator.";
  }
  if (code === "ROLE_INACTIVE") {
    return "Role is inactive. Contact administrator.";
  }
  return null;
}

export function messageForAuthCode(code, fallback) {
  if (code === "ACCOUNT_INACTIVE") {
    return "Your user account is inactive. Contact an administrator.";
  }
  if (code === "ROLE_INACTIVE") {
    return "Your assigned role is inactive. Contact an administrator.";
  }
  if (fallback && fallback !== "Unauthorized access") {
    return fallback;
  }
  return "Your session has expired. Please sign in again.";
}

function friendlyAuthMessage(code, fallback) {
  return messageForAuthCode(code, fallback);
}

export function shouldEndAuthSession(status, code) {
  if (status === 401) return true;
  if (status === 403 && SESSION_END_CODES.has(code)) return true;
  return false;
}

export function storeAuthSessionMessage(message) {
  if (!message) return;
  try {
    sessionStorage.setItem(AUTH_SESSION_MESSAGE_KEY, message);
  } catch {
    /* ignore */
  }
}

export function clearAuthSessionMessage() {
  try {
    sessionStorage.removeItem(AUTH_SESSION_MESSAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Remove stored logout reason and ?reason= from the login URL. */
export function clearLoginRedirectReason() {
  clearAuthSessionMessage();
  if (typeof window !== "undefined" && window.history?.replaceState) {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("reason")) {
        url.searchParams.delete("reason");
        const next = url.pathname + (url.search || "");
        window.history.replaceState({}, "", next);
      }
    } catch {
      /* ignore */
    }
  }
}

/** Read logout reason without removing (React StrictMode-safe). */
export function readAuthSessionNotice() {
  try {
    const fromStorage = sessionStorage.getItem(AUTH_SESSION_MESSAGE_KEY);
    if (fromStorage) return fromStorage;
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const reason = params.get("reason");
      if (reason) return reason;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Clear local session and redirect to login (full page reload). */
export function endAuthSession(message) {
  tokenManager.clearAuth();
  clearAppRole();
  if (message) {
    storeAuthSessionMessage(message);
  } else {
    clearAuthSessionMessage();
  }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    const reason = message ? `?reason=${encodeURIComponent(message)}` : "";
    window.location.href = `/login${reason}`;
  }
}

/**
 * @returns {boolean} true when the caller should stop (redirect started)
 */
export function handleAuthApiFailure(status, code, message, data) {
  if (data && typeof data === "object" && data.loggedIn === false) {
    endAuthSession(
      friendlyAuthMessage(code, message || data.message),
    );
    return true;
  }
  if (shouldEndAuthSession(status, code)) {
    endAuthSession(friendlyAuthMessage(code, message));
    return true;
  }
  return false;
}

/** @deprecated Prefer readAuthSessionNotice + clearAuthSessionMessage */
export function consumeAuthSessionMessage() {
  const message = readAuthSessionNotice();
  if (message) clearAuthSessionMessage();
  return message;
}
